import { Table, Form, Button, Dropdown } from 'react-bootstrap';
import { useGetStoreExpensesQuery } from '../../slices/storeExpensesApiSlice';
import { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const StoreExpenseInvoices = () => {
  const { data: storeExpensesData } = useGetStoreExpensesQuery({});

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [detailsInput, setDetailsInput] = useState('');

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const categories = useMemo(() => {
    if (!storeExpensesData?.storeExpenses) return [];
    return [...new Set(storeExpensesData.storeExpenses.map(e => e.category).filter(Boolean))];
  }, [storeExpensesData]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat)
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    );
  };

  const filteredStoreExpenses = useMemo(() => {
    if (!storeExpensesData?.storeExpenses) return [];

    return storeExpensesData.storeExpenses.filter(exp => {
      const expDate = new Date(exp.date).toISOString().split('T')[0];

      const matchesDate =
        (!startDate || expDate >= startDate) &&
        (!endDate || expDate <= endDate);

      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.includes(exp.category);

      const matchesDetails =
        detailsInput === '' ||
        exp.details?.toLowerCase().includes(detailsInput.toLowerCase());

      return matchesDate && matchesCategory && matchesDetails;
    });
  }, [storeExpensesData, startDate, endDate, selectedCategories, detailsInput]);

  const groupedSummary = useMemo(() => {
    const summary = {};

    filteredStoreExpenses.forEach(exp => {
      const cat = exp.category || 'Uncategorized';
      const note = exp.notes?.trim() || '[Blank]';

      if (!summary[cat]) summary[cat] = {};
      if (!summary[cat][note]) {
        summary[cat][note] = {
          details: exp.details,
          total: Number(exp.amount || 0),
          date: exp.date,
        };
      } else {
        summary[cat][note].total += Number(exp.amount || 0);
      }
    });

    return summary;
  }, [filteredStoreExpenses]);

  // GRAND TOTAL
  const grandTotal = useMemo(() => {
    let total = 0;
    Object.values(groupedSummary).forEach(notes => {
      Object.values(notes).forEach(entry => {
        total += entry.total;
      });
    });
    return total;
  }, [groupedSummary]);

  // SORTING (date ASC by default)
  const sortedSummary = useMemo(() => {
    const sorted = {};

    Object.keys(groupedSummary).forEach(cat => {
      const notes = Object.entries(groupedSummary[cat]);

      notes.sort((a, b) => {
        let valA = new Date(a[1].date);
        let valB = new Date(b[1].date);

        if (sortConfig.key && sortConfig.key !== 'date') {
          valA = a[1][sortConfig.key];
          valB = b[1][sortConfig.key];
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });

      sorted[cat] = Object.fromEntries(notes);
    });

    return sorted;
  }, [groupedSummary, sortConfig]);

  const paginatedCategories = useMemo(() => {
    const allRows = [];

    Object.entries(sortedSummary).forEach(([cat, notes]) => {
      allRows.push({ type: 'category', cat });

      Object.entries(notes).forEach(([note, entry]) => {
        allRows.push({
          type: 'row',
          cat,
          note,
          ...entry,
        });
      });
    });

    const start = (currentPage - 1) * pageSize;
    return allRows.slice(start, start + pageSize);
  }, [sortedSummary, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    let count = 0;
    Object.values(sortedSummary).forEach(notes => {
      count += 1 + Object.keys(notes).length;
    });
    return Math.ceil(count / pageSize);
  }, [sortedSummary, pageSize]);

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleExport = () => {
    const rows = [];

    Object.entries(sortedSummary).forEach(([cat, notes]) => {
      Object.entries(notes).forEach(([note, entry]) => {
        rows.push({
          Category: cat,
          Note: note,
          Details: entry.details,
          Date: entry.date ? new Date(entry.date).toLocaleDateString('en-US') : '',
          Total: entry.total,
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer]), 'StoreExpense_Summary.xlsx');
  };

  return (
    <div className="container mt-4">
      <h1 className="text-center">Invoice Summary</h1>

      <h4 className="text-center mb-3">
        Grand Total: {grandTotal.toFixed(2)}
      </h4>

      <Form className="mb-3 d-flex justify-content-center flex-wrap">
        <Form.Group className="mx-2">
          <Form.Label>Start Date</Form.Label>
          <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </Form.Group>

        <Form.Group className="mx-2">
          <Form.Label>End Date</Form.Label>
          <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </Form.Group>

        <Dropdown className="mx-2">
          <Dropdown.Toggle variant="secondary">Select Categories</Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {categories.map(cat => (
              <Form.Check
                key={cat}
                type="checkbox"
                label={cat}
                checked={selectedCategories.includes(cat)}
                onChange={() => toggleCategory(cat)}
                className="px-3"
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Form.Group className="mx-2">
          <Form.Label>Details Filter</Form.Label>
          <Form.Control
            type="text"
            value={detailsInput}
            onChange={e => setDetailsInput(e.target.value)}
            placeholder="Search details..."
          />
        </Form.Group>
      </Form>

      <Table striped bordered hover responsive>
        <thead className="text-center">
          <tr>
            <th onClick={() => requestSort('note')}>Note</th>
            <th onClick={() => requestSort('details')}>Details</th>
            <th onClick={() => requestSort('date')}>Date</th>
            <th onClick={() => requestSort('total')}>Total</th>
          </tr>
        </thead>

        <tbody>
          {paginatedCategories.map((item, idx) =>
            item.type === 'category' ? (
              <tr key={idx} className="table-primary fw-bold">
                <td colSpan={4}>{item.cat}</td>
              </tr>
            ) : (
              <tr key={idx}>
                <td>{item.note}</td>
                <td>{item.details}</td>
                <td>{item.date ? new Date(item.date).toLocaleDateString('en-US') : '-'}</td>
                <td>{item.total.toFixed(2)}</td>
              </tr>
            )
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <Form.Select
          style={{ width: '100px' }}
          value={pageSize}
          onChange={e => setPageSize(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </Form.Select>

        <div>
          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
            className="mx-1"
          >
            Prev
          </Button>

          <span>Page {currentPage} of {totalPages}</span>

          <Button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
            className="mx-1"
          >
            Next
          </Button>
        </div>

        <Button variant="success" onClick={handleExport}>
          Export to Excel
        </Button>
      </div>
    </div>
  );
};

export default StoreExpenseInvoices;